// Side-effect helpers for sending open-house invite emails. Keeps the
// per-mode email-firing logic out of components so both the "auto-send"
// path (vacant home — fires immediately when the open house is created)
// and the "wait for seller OK, then send" path (occupied home — fires
// from a Send button later) share the same implementation.

import { getDoc, doc as docRef } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { sendOpenHouseEmail } from "./emailSender";
import {
  composeSchedulingSellerEmail,
  composeSchedulingAgentEmail,
} from "./openHouseEmailComposer";
import { chooseListingAgentRecipient } from "./openHouseHelpers";

// Fire the seller + hosting-agent "open house scheduled" emails for a saved
// open house. Reads the doc fresh from Firestore so the caller can pass just
// the id (createOpenHouse returns only { id }). Silently no-ops on either
// contact if the email is missing.
export async function sendSchedulingEmails(openHouseId, user, templates) {
  const snap = await getDoc(docRef(firestored, "openHouses", openHouseId));
  if (!snap.exists()) return;
  const oh = { id: openHouseId, ...snap.data() };
  const dealId = oh.dealId;

  const tasks = [];

  if (oh.sellerEmail) {
    const { subject, text, html } = composeSchedulingSellerEmail(
      oh,
      user,
      templates?.["scheduling-seller"],
    );
    tasks.push(
      sendOpenHouseEmail({
        userId: user.id,
        to: oh.sellerEmail,
        toName: oh.sellerName,
        subject,
        text,
        html,
        type: "scheduling",
        relatedDealId: dealId,
        relatedOpenHouseId: openHouseId,
      }),
    );
  }
  if (oh.hostingAgentEmail) {
    const { subject, text, html } = composeSchedulingAgentEmail(
      oh,
      user,
      undefined,
      templates?.["scheduling-agent"],
    );
    tasks.push(
      sendOpenHouseEmail({
        userId: user.id,
        to: oh.hostingAgentEmail,
        toName: oh.hostingAgentName,
        subject,
        text,
        html,
        type: "scheduling",
        relatedDealId: dealId,
        relatedOpenHouseId: openHouseId,
      }),
    );
  }

  {
    const { subject, text, html } = composeSchedulingAgentEmail(
      oh,
      user,
      undefined,
      templates?.["scheduling-agent"],
    );
    tasks.push(
      sendListingAgentCopy({
        oh,
        sender: user,
        subject,
        text,
        html,
        type: "scheduling",
        excludeEmails: [oh.sellerEmail, oh.hostingAgentEmail],
      }),
    );
  }

  return Promise.all(tasks);
}

// Send the listing agent (the agent on the parent deal) their own copy of an
// open-house email. Resolves oh.dealId -> deal.agentData -> user -> email,
// dedupes against people already emailed in this flow, and is best-effort:
// any failure is logged and swallowed so it never blocks the primary sends.
//
//   sendListingAgentCopy({ oh, sender, subject, text, html, type, excludeEmails })
//
// Returns one of:
//   <sendOpenHouseEmail result>                    // attempted a send
//   { ok: false, reason: "no-deal" }               // no dealId / deal missing
//   { ok: false, reason: "no-agent" }              // no agentData / user missing
//   { ok: false, reason: "skip" }                  // no email or deduped
//   { ok: false, reason: "error", error }          // unexpected throw
export async function sendListingAgentCopy({
  oh,
  sender,
  subject,
  text,
  html,
  type,
  excludeEmails = [],
}) {
  try {
    const dealId = oh?.dealId;
    if (!dealId) return { ok: false, reason: "no-deal" };

    const dealSnap = await getDoc(docRef(firestored, "deals", String(dealId)));
    if (!dealSnap.exists()) return { ok: false, reason: "no-deal" };

    const agentId = dealSnap.data()?.agentData;
    if (!agentId) return { ok: false, reason: "no-agent" };

    const userSnap = await getDoc(docRef(firestored, "users", String(agentId)));
    if (!userSnap.exists()) return { ok: false, reason: "no-agent" };

    const u = userSnap.data() || {};
    const recipient = chooseListingAgentRecipient({
      agentEmail: u.email,
      agentName: `${u.firstname || ""} ${u.lastname || ""}`.trim(),
      excludeEmails,
    });
    if (!recipient) return { ok: false, reason: "skip" };

    return await sendOpenHouseEmail({
      userId: sender.id,
      to: recipient.email,
      toName: recipient.name,
      subject,
      text,
      html,
      type,
      relatedDealId: dealId,
      relatedOpenHouseId: oh.id,
    });
  } catch (err) {
    console.error("sendListingAgentCopy failed:", err);
    return { ok: false, reason: "error", error: err?.message };
  }
}
